import React from 'react';
import DataImporter from './index';
import { Accordion, Table } from 'react-bootstrap';
import { createTasks } from '../../api/tasksAPI';
import handleDownloadTemplateHelper from './handleDownloadTemplateHelper';


const TaskImporter = ({ assetId, onComplete }) => {
  const handleDataImport = async (rows, onProgress) => {
    let count = 0;
    for (const row of rows) {
      try {
        const normalise = (value) => (value === '' || value === undefined ? undefined : value);

        const resolvedAssetId = normalise(assetId ?? row.assetId ?? row.asset_id);
        if (!resolvedAssetId) {
          throw new Error('Asset Id is required for each task');
        }

        await createTasks({
          name: row.name,
          codeId: row?.code || null,
          assetId: resolvedAssetId,
          taskTypeId: row.taskTypeId,
          frequency: row.frequency,
          assetMeterId: row.assetMeterId,
          maintenancePlanId: row?.maintenancePlanId || null,
          planningHorizon: row?.leadTime || 2,
          planningTask: row?.planningTask ?? 0,
          calendarBased: row?.calendarBased ?? 1,
          duration: row?.duration || 0,
          labourHours: row?.labourHours || 0,
          miscPrice: row?.miscPrice || 0,
          partsPrice: row?.partsPrice || 0,
          labourCost: row?.labourCost || 0,
          lastDone: row?.lastDone || 0,
          lastDoneDate: row?.lastDoneDate || null,
          source: 'UPLOAD',
          modifierCodeId: row?.modifierCode || null,
          reference: row?.reference || null,
          partsExpense: row.partsExpense,
          labourExpense: row.labourExpense,
          miscExpense: row.miscExpense,
          defaultCostCentre: row.defaultCostCentre, // use default from settings
          statutory: row.statutory,
          schedulingType: row?.schedulingType || 'simple',
          forecastBasis: row?.forecastBasis || row?.forecast_basis || 'lastdone',
          schedulingGroupId: row?.schedulingGroupId || null,
          schedulingWindow: row?.schedulingWindow || 24,
          schedulingPriority: row?.schedulingPriority || 1,
          partsOverride: 1,
          labourOverride: 1,
          servicesOverride: 1,
        });

      } catch (err) {
        console.error('Failed to import task', err);
      }
      count += 1;
      if (onProgress) onProgress(count);
    }
  };

  return (
    <>
      <div className="card h-100 p-16 rounded-0">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <p className="mb-0 flex-grow-1">
            You can bulk import <strong>tasks</strong> into the system using an Excel or CSV file.
          </p>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm ms-3"
            onClick={() => handleDownloadTemplateHelper('tasks')}
          >
            Download Template
          </button>
        </div>

        <p>Expand the lists below to see the required and optional fields.</p>
        <div className='mb-4'>
          <Accordion alwaysOpen={false}>
            <Accordion.Item eventKey='0'>
              <Accordion.Header>Required Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file must include the following column headers:</p>
                <Table bordered size='sm'>
                  <thead className='table-light'>
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!assetId && (
                      <tr>
                        <td>assetId</td>
                        <td>Lookup</td>
                        <td>Asset number to link the task (required when not importing from an Asset page)</td>
                      </tr>
                    )}
                    <tr><td>name</td><td>Text</td><td>Task name</td></tr>
                    <tr><td>frequency</td><td>Number</td><td>Frequency (days)</td></tr>
                    <tr><td>taskTypeId</td><td>Lookup</td><td>Valid task type</td></tr>
                    <tr><td>planningTask</td><td>Number</td><td>1 if planning task</td></tr>
                    <tr><td>calendarBased</td><td>Number</td><td>1 if calendar based</td></tr>
                    <tr><td>assetMeterId</td><td>Lookup</td><td>Meter reference - required if task is meter-based</td></tr>
                    <tr><td>leadTime</td><td>Lookup</td><td>Lead time - required if Planning Task = 1</td></tr>
                    <tr><td>defaultCostCentre</td><td>Lookup</td><td>Cost centre to apply</td></tr>
                    <tr><td>partsExpense</td><td>Lookup</td><td>Parts expense code</td></tr>
                    <tr><td>labourExpense</td><td>Lookup</td><td>Labour expense code</td></tr>
                    <tr><td>miscExpense</td><td>Lookup</td><td>Other expense code</td></tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey='1'>
              <Accordion.Header>Optional Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file can optionally include the following column headers:</p>
                <Table bordered size='sm'>
                  <thead className='table-light'>
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>code</td><td>Text</td><td>Task code</td></tr>
                    <tr><td>modifierCode</td><td>Number</td><td>Modifier code</td></tr>
                    <tr><td>lastDone</td><td>Number</td><td>Previous completion meter value</td></tr>
                    <tr><td>lastDoneDate</td><td>Date</td><td>Previous completion date</td></tr>
                    {/*<tr><td>maintenancePlanId</td><td>Lookup</td><td>Maintenance plan</td></tr>*/}
                    <tr><td>duration</td><td>Number</td><td>Duration in hours</td></tr>
                    <tr><td>labourHours</td><td>Number</td><td>Labour hours</td></tr>
                    <tr><td>miscPrice</td><td>Number</td><td>Other price</td></tr>
                    <tr><td>partsPrice</td><td>Number</td><td>Parts price</td></tr>
                    <tr><td>labourCost</td><td>Number</td><td>Labour cost</td></tr>
                    <tr><td>reference</td><td>Text</td><td>Reference text</td></tr>
                    <tr><td>statutory</td><td>Number</td><td>Compliance flag</td></tr>
                    <tr><td>schedulingType / scheduling_type</td><td>Text</td><td>Scheduling behaviour (simple, suppression, cycle)</td></tr>
                    <tr><td>schedulingGroupId</td><td>Lookup</td><td>Scheduling group (suppression only)</td></tr>
                    <tr><td>schedulingWindow</td><td>Number</td><td>Scheduling window in hours</td></tr>
                    <tr><td>schedulingPriority</td><td>Number</td><td>Scheduling priority order</td></tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <DataImporter
          entity='tasks'
          requiredFields={[
            { key: 'name', type: 'string' },
            { key: 'code', type: 'lookup', api: 'task-codes', required: false, nameField: 'code' },
            { key: 'modifierCode', type: 'lookup', api: 'modifier-codes', nameField: 'code', required: false },
            { key: 'taskTypeId', type: 'lookup', required: true, api: 'task-types', nameField: 'taskTypeName' },
            { key: 'leadTime', type: 'lookup', required: true, api: 'planning-horizon', nameField: 'name' },
            {
              key: 'assetId',
              type: 'lookup',
              required: true,
              api: 'assets',
              nameField: 'assetNumber',
            },
            { key: 'frequency', type: 'number' },
            { key: 'lastDone', type: 'number' },
            { key: 'lastDoneDate', type: 'date' },
            { key: 'assetMeterId', type: 'lookup', api: 'meters', nameField: 'name', assetId },
            { key: 'planningTask', type: 'number' },
            { key: 'calendarBased', type: 'number' },
            { key: 'duration', type: 'number' },
            { key: 'labourHours', type: 'number' },
            { key: 'miscPrice', type: 'number' },
            { key: 'partsPrice', type: 'number' },
            { key: 'labourCost', type: 'number' },
            { key: 'defaultCostCentre', type: 'lookup', required: true, api: 'cost-centres', nameField: 'costCenterDescription' },
            { key: 'partsExpense', type: 'lookup', required: true, api: 'expense-codes', nameField: 'name' },
            { key: 'labourExpense', type: 'lookup', required: true, api: 'expense-codes', nameField: 'name' },
            { key: 'miscExpense', type: 'lookup', required: true, api: 'expense-codes', nameField: 'name' },
            { key: 'reference', type: 'string' },
            { key: 'statutory', type: 'number' },
            { key: 'schedulingType', type: 'string' },
            { key: 'scheduling_type', type: 'string' },
            { key: 'schedulingGroupId', type: 'lookup', api: 'scheduling-group', nameField: 'name' },
            { key: 'schedulingWindow', type: 'number' },
            { key: 'schedulingPriority', type: 'number' },
            { key: 'maintenancePlanId', type: 'lookup', api: 'maintenance-plans', nameField: 'name' },
          ]}
          onCommit={handleDataImport}
          onComplete={onComplete}
        />
      </div>
    </>
  );
};

export default TaskImporter;
